import { Modal } from '@/components/admin/Modal';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import type { DataSourceTablePreviewResponse } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';

interface Props {
  dataSourceId: string;
  tableId: string;
  tableName: string;
  open: boolean;
  onClose: () => void;
}

/**
 * 데이터소스 테이블 즉시 미리보기 — 1000행 / 100컬럼 / 60초 타임아웃.
 * SavedQueryPreviewModal 의 본문 표시 패턴을 재사용한다.
 */
export function TablePreviewModal({ dataSourceId, tableId, tableName, open, onClose }: Props) {
  const query = useQuery<DataSourceTablePreviewResponse, Error>({
    queryKey: ['admin', 'data-sources', dataSourceId, 'tables', tableId, 'preview'],
    queryFn: ({ signal }) => api.dataSourceTablePreview(dataSourceId, tableId, signal),
    enabled: open,
    staleTime: 0,
    gcTime: 30_000,
    retry: false,
  });

  return (
    <Modal open={open} onClose={onClose} title={`테이블 미리보기 — ${tableName}`} size="xl">
      <div className="space-y-3">
        {query.isPending ? (
          <div className="py-8 text-center text-sm text-foreground-muted">실행 중…</div>
        ) : query.isError ? (
          <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {query.error instanceof ApiError ? query.error.message : '미리보기 실행 실패'}
          </div>
        ) : query.data ? (
          <PreviewBody preview={query.data} />
        ) : null}
      </div>
      <div className="flex justify-end pt-3">
        <Button variant="ghost" onClick={onClose}>
          닫기
        </Button>
      </div>
    </Modal>
  );
}

function PreviewBody({ preview }: { preview: DataSourceTablePreviewResponse }) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-muted">
        <span className="font-mono">
          "{preview.schemaName}"."{preview.tableName}"
        </span>
        <span className="font-mono">· {preview.rowCount}행</span>
        <span className="font-mono">· {preview.columns.length}컬럼</span>
        {preview.truncated ? (
          <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-mono text-amber-700 dark:text-amber-400">
            1000행 이상 — 자름
          </span>
        ) : null}
        {preview.truncatedColumns ? (
          <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-mono text-amber-700 dark:text-amber-400">
            100컬럼 이상 — 처음 100개만 표시
          </span>
        ) : null}
        <span className="font-mono">· {preview.latencyMs}ms</span>
      </div>
      <div className="rounded border border-border bg-surface overflow-auto max-h-[70vh]">
        {preview.columns.length === 0 ? (
          <div className="p-3 text-xs text-foreground-muted">빈 결과</div>
        ) : (
          <table className="w-full text-[11px]">
            <thead className="bg-surface-muted/40 sticky top-0 z-10">
              <tr>
                {preview.columns.map((c) => (
                  <th
                    key={c}
                    className="px-2 py-1.5 text-left font-medium font-mono uppercase text-foreground-muted whitespace-nowrap"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((row, ri) => (
                <tr
                  // biome-ignore lint/suspicious/noArrayIndexKey: preview 결과 정적 렌더
                  key={ri}
                  className="border-t border-border"
                >
                  {row.map((cell, ci) => (
                    <td
                      // biome-ignore lint/suspicious/noArrayIndexKey: column index 는 컬럼 순서와 일치
                      key={ci}
                      className="px-2 py-1 font-mono whitespace-nowrap align-top"
                      title={formatCell(cell)}
                    >
                      {formatCell(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}
